import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { DegreeOutput, NationalityOutput, UserDetail } from 'types/api'

import { Card } from '@/components/ui/card'

type UserWidgetProps = {
  user: UserDetail
  nationality: NationalityOutput | null
  degree: DegreeOutput | null
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="truncate font-semibold text-ink">{value || '—'}</div>
    </div>
  )
}

export function UserWidget(props: UserWidgetProps) {
  const { t } = useTranslation('users')
  const { user, nationality, degree } = props

  const {
    first_name,
    last_name,
    email,
    phone,
    birth_date,
    role,
    sex,
    bank,
    bank_account,
    has_contract
  } = user

  const initials =
    `${first_name?.[0] ?? ''}${last_name?.[0] ?? ''}`.toUpperCase() ||
    email?.[0]?.toUpperCase() ||
    '?'

  return (
    <Card className="flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:gap-6">
      <div className="flex items-center gap-3.5 lg:shrink-0">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-base font-bold text-primary">
          {initials}
        </span>
        <div className="min-w-0">
          <div className="truncate font-display text-lg font-bold tracking-tight text-ink">
            {`${first_name ?? ''} ${last_name ?? ''}`.trim() || '—'}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {role && (
              <span className="inline-flex items-center rounded-full bg-ink/[0.06] px-2 py-0.5 text-[11px] font-semibold text-ink/60">
                {role}
              </span>
            )}
            {has_contract && (
              <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                {t('form.labels.has_contract')}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="hidden h-12 w-px shrink-0 bg-ink/[0.08] lg:block" />

      <div className="flex flex-1 flex-wrap gap-x-7 gap-y-3.5">
        <Stat label={t('form.labels.email')} value={email} />
        <Stat label={t('form.labels.phone')} value={phone} />
        <Stat
          label={t('form.labels.birthDate')}
          value={birth_date ? new Date(birth_date).toLocaleDateString() : ''}
        />
        <Stat
          label={t('form.labels.sex')}
          value={sex ? t(`form.labels.${sex.toLowerCase()}`) : ''}
        />
        <Stat label={t('form.labels.nationality')} value={nationality?.name} />
        <Stat label={t('form.labels.degree')} value={degree?.name} />
        <Stat label={t('form.labels.bank')} value={bank} />
        <Stat label={t('form.labels.bankAccount')} value={bank_account} />
      </div>
    </Card>
  )
}
